import { useEffect, useRef, useState } from "react";
import { api, formatApiError, brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Loader2, Upload, Pencil, Trash2, X, Gift, Trophy, Check, Clock, Ban, Settings2, ScanLine,
} from "lucide-react";
import { toast } from "sonner";

function RewardImage({ rewardId, bust = 0, className = "" }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let revoke = null;
    (async () => {
      try {
        const resp = await api.get(`/rewards/${rewardId}/image?v=${bust}`, { responseType: "blob" });
        const u = URL.createObjectURL(resp.data);
        revoke = u;
        setUrl(u);
      } catch {}
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [rewardId, bust]);
  if (!url) {
    return (
      <div className={`flex items-center justify-center bg-zinc-900 border border-zinc-800 ${className}`}>
        <Loader2 className="h-5 w-5 text-zinc-600 animate-spin" />
      </div>
    );
  }
  return <img src={url} alt="Recompensa" className={`object-cover ${className}`} />;
}

export default function OwnerLoyalty() {
  const [config, setConfig] = useState({ points_per_amount: 1, amount_unit: 10 });
  const [savingCfg, setSavingCfg] = useState(false);
  const [rewards, setRewards] = useState([]);
  const [loadingRewards, setLoadingRewards] = useState(true);
  const [redemptions, setRedemptions] = useState([]);
  const [loadingReds, setLoadingReds] = useState(true);
  const [bust, setBust] = useState(0);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", points_cost: "", is_active: true });
  const [file, setFile] = useState(null);
  const [removeImg, setRemoveImg] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [actingId, setActingId] = useState("");
  const [filterStatus, setFilterStatus] = useState("pending");
  const fileRef = useRef(null);

  const loadAll = async () => {
    try {
      setLoadingRewards(true);
      setLoadingReds(true);
      const [cfg, rw, rd] = await Promise.all([
        api.get("/loyalty/config"),
        api.get("/rewards"),
        api.get("/redemptions"),
      ]);
      setConfig(cfg.data);
      setRewards(rw.data);
      setRedemptions(rd.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingRewards(false);
      setLoadingReds(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      const { data } = await api.patch("/loyalty/config", {
        points_per_amount: Number(config.points_per_amount),
        amount_unit: Number(config.amount_unit),
      });
      setConfig(data);
      toast.success("Régua de pontos atualizada");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingCfg(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", description: "", points_cost: "", is_active: true });
    setFile(null); setRemoveImg(false);
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, description: r.description || "", points_cost: String(r.points_cost), is_active: !!r.is_active });
    setFile(null); setRemoveImg(false);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Nome obrigatório"); return; }
    const pc = Number(form.points_cost);
    if (!pc || pc < 1) { toast.error("Custo em pontos inválido"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("description", form.description);
      fd.append("points_cost", String(pc));
      fd.append("is_active", form.is_active ? "true" : "false");
      if (file) fd.append("file", file);
      if (editing && removeImg && !file) fd.append("remove_image", "1");
      const cfg = { headers: { "Content-Type": "multipart/form-data" } };
      if (editing) {
        const { data } = await api.patch(`/rewards/${editing.id}`, fd, cfg);
        setRewards((p) => p.map((x) => (x.id === data.id ? data : x)));
        toast.success("Recompensa atualizada");
      } else {
        const { data } = await api.post("/rewards", fd, cfg);
        setRewards((p) => [data, ...p]);
        toast.success("Recompensa criada");
      }
      setBust(Date.now());
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    setDeletingId(r.id);
    try {
      await api.delete(`/rewards/${r.id}`);
      setRewards((p) => p.filter((x) => x.id !== r.id));
      toast.success("Recompensa removida");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeletingId("");
    }
  };

  const confirmRedemption = async (red) => {
    setActingId(red.id);
    try {
      const { data } = await api.post(`/redemptions/${red.id}/confirm`);
      setRedemptions((p) => p.map((x) => (x.id === data.id ? data : x)));
      toast.success(`Resgate ${data.code} confirmado — pontos debitados`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActingId("");
    }
  };
  const cancelRedemption = async (red) => {
    setActingId(red.id);
    try {
      const { data } = await api.post(`/redemptions/${red.id}/cancel`);
      setRedemptions((p) => p.map((x) => (x.id === data.id ? data : x)));
      toast.success("Resgate cancelado");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActingId("");
    }
  };

  const filteredRed = redemptions.filter((r) => filterStatus === "all" ? true : r.status === filterStatus);
  const pendingCount = redemptions.filter((r) => r.status === "pending").length;

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <div className="text-[11px] font-mono-caps text-zinc-500">Fidelidade</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase text-zinc-100 flex items-center gap-2">
          <Trophy className="h-7 w-7 text-amber-brand" /> Clube de pontos
        </h1>
        <p className="text-sm text-zinc-400 mt-1">
          Configure a régua, cadastre recompensas e confirme resgates pendentes.
        </p>
      </div>

      <div data-testid="loyalty-config-card" className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-5 md:p-7">
        <div className="flex items-center gap-2 mb-4">
          <Settings2 className="h-4 w-4 text-green-brand" />
          <h2 className="font-display text-lg font-bold uppercase text-zinc-100">Régua de pontos</h2>
        </div>
        <p className="text-sm text-zinc-400 mb-3">
          Padrão: 1 ponto a cada R$ 10 gastos em serviços aprovados e finalizados.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div>
            <Label className="text-xs font-mono-caps text-zinc-400">Ganha</Label>
            <Input
              data-testid="loyalty-points-input"
              type="number" min="1" max="1000"
              value={config.points_per_amount}
              onChange={(e) => setConfig((p) => ({ ...p, points_per_amount: e.target.value }))}
              className="mt-1 bg-zinc-900 border-zinc-800"
            />
            <div className="text-[10px] text-zinc-500 mt-1">pontos</div>
          </div>
          <div>
            <Label className="text-xs font-mono-caps text-zinc-400">A cada</Label>
            <Input
              data-testid="loyalty-amount-input"
              type="number" min="0.01" step="0.01"
              value={config.amount_unit}
              onChange={(e) => setConfig((p) => ({ ...p, amount_unit: e.target.value }))}
              className="mt-1 bg-zinc-900 border-zinc-800"
            />
            <div className="text-[10px] text-zinc-500 mt-1">reais gastos</div>
          </div>
          <Button
            data-testid="loyalty-config-save-btn"
            onClick={saveConfig}
            disabled={savingCfg}
            className="h-11 bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase"
          >
            {savingCfg ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar régua"}
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-5 md:p-7">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Gift className="h-4 w-4 text-amber-brand" />
              <h2 className="font-display text-lg font-bold uppercase text-zinc-100">Recompensas</h2>
            </div>
            <p className="text-sm text-zinc-400 mt-1">
              Os clientes trocam seus pontos pelas recompensas ativas.
            </p>
          </div>
          <Button
            data-testid="reward-new-btn"
            onClick={openCreate}
            className="self-start sm:self-auto bg-amber-brand hover:bg-amber-brandDark text-black font-bold uppercase"
          >
            <Plus className="h-4 w-4 mr-1" /> Nova recompensa
          </Button>
        </div>
        {loadingRewards ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-amber-brand" /></div>
        ) : rewards.length === 0 ? (
          <div data-testid="rewards-empty" className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950/40 p-8 text-center">
            <Gift className="h-8 w-8 mx-auto text-zinc-700" />
            <p className="mt-2 text-sm text-zinc-500">Nenhuma recompensa cadastrada ainda.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rewards.map((r) => (
              <div key={r.id} data-testid={`reward-card-${r.id}`} className="rounded-xl border border-zinc-800 bg-zinc-900/50 overflow-hidden flex flex-col">
                {r.has_image ? (
                  <RewardImage rewardId={r.id} bust={bust} className="h-36 w-full" />
                ) : (
                  <div className="h-36 w-full flex items-center justify-center bg-gradient-to-br from-amber-900/40 to-zinc-900">
                    <Gift className="h-10 w-10 text-amber-brand/70" />
                  </div>
                )}
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display text-lg font-bold text-zinc-100 line-clamp-2">{r.name}</h3>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${r.is_active ? "text-green-300 bg-green-brand/15 border border-green-brand/40" : "text-zinc-400 bg-zinc-800 border border-zinc-700"}`}>
                      {r.is_active ? "Ativa" : "Inativa"}
                    </span>
                  </div>
                  {r.description && <p className="mt-1 text-sm text-zinc-400 line-clamp-3">{r.description}</p>}
                  <div className="mt-3 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-amber-brand font-bold">
                      <Trophy className="h-4 w-4" /> {r.points_cost} pts
                    </span>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <Button data-testid={`reward-edit-${r.id}`} variant="ghost" size="sm" onClick={() => openEdit(r)} className="text-amber-300 hover:bg-amber-950/40">
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                    </Button>
                    <Button data-testid={`reward-delete-${r.id}`} variant="ghost" size="sm" disabled={deletingId === r.id} onClick={() => remove(r)} className="text-red-400 hover:bg-red-950/40">
                      {deletingId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (<><Trash2 className="h-3.5 w-3.5 mr-1" /> Excluir</>)}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-5 md:p-7">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2">
              <ScanLine className="h-4 w-4 text-green-brand" />
              <h2 className="font-display text-lg font-bold uppercase text-zinc-100">
                Resgates
                {pendingCount > 0 && (
                  <span className="ml-2 inline-flex items-center gap-1 text-xs font-sans font-bold uppercase text-amber-brand bg-amber-brand/10 border border-amber-brand/40 rounded-full px-2 py-0.5">
                    <Clock className="h-3 w-3" /> {pendingCount} pendente{pendingCount === 1 ? "" : "s"}
                  </span>
                )}
              </h2>
            </div>
            <p className="text-sm text-zinc-400 mt-1">Confira o código do cliente na entrega e confirme para debitar os pontos.</p>
          </div>
          <div className="flex flex-wrap gap-1 self-start sm:self-auto">
            {[
              { k: "pending", l: "Pendentes" },
              { k: "confirmed", l: "Confirmados" },
              { k: "cancelled", l: "Cancelados" },
              { k: "all", l: "Todos" },
            ].map((f) => (
              <button
                key={f.k}
                data-testid={`red-filter-${f.k}`}
                onClick={() => setFilterStatus(f.k)}
                className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md border ${filterStatus === f.k ? "border-green-brand text-green-300 bg-green-brand/10" : "border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}
              >
                {f.l}
              </button>
            ))}
          </div>
        </div>
        {loadingReds ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-green-brand" /></div>
        ) : filteredRed.length === 0 ? (
          <div data-testid="redemptions-empty" className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950/40 p-8 text-center">
            <ScanLine className="h-8 w-8 mx-auto text-zinc-700" />
            <p className="mt-2 text-sm text-zinc-500">Nenhum resgate nesta aba.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredRed.map((r) => (
              <div key={r.id} data-testid={`redemption-row-${r.id}`} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <code
                      data-testid={`redemption-code-${r.id}`}
                      className="text-sm font-mono-caps font-black bg-zinc-950 text-amber-brand border border-amber-brand/40 rounded px-2 py-1"
                    >{r.code}</code>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                      r.status === "pending" ? "text-amber-200 bg-amber-brand/10 border-amber-brand/40"
                      : r.status === "confirmed" ? "text-green-300 bg-green-brand/15 border-green-brand/40"
                      : "text-red-300 bg-red-950/40 border-red-900"
                    }`}>{r.status === "pending" ? "Pendente" : r.status === "confirmed" ? "Confirmado" : "Cancelado"}</span>
                  </div>
                  <div className="mt-1 text-zinc-100 font-semibold truncate">{r.reward_name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5" translate="no">
                    <span>{r.customer_name || r.customer_id}</span>
                    <span className="mx-1">·</span>
                    <span>{r.points_cost} pts</span>
                    {r.created_at && (<><span className="mx-1">·</span><span>{new Date(r.created_at).toLocaleString("pt-BR")}</span></>)}
                  </div>
                </div>
                {r.status === "pending" && (
                  <div className="flex gap-2">
                    <Button
                      data-testid={`redemption-confirm-${r.id}`}
                      disabled={actingId === r.id}
                      onClick={() => confirmRedemption(r)}
                      className="h-9 bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase"
                    >
                      {actingId === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Check className="h-4 w-4 mr-1" /> Confirmar</>)}
                    </Button>
                    <Button
                      data-testid={`redemption-cancel-${r.id}`}
                      variant="ghost"
                      disabled={actingId === r.id}
                      onClick={() => cancelRedemption(r)}
                      className="h-9 text-red-400 hover:bg-red-950/40"
                    >
                      <Ban className="h-4 w-4 mr-1" /> Cancelar
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display uppercase flex items-center gap-2">
              <Gift className="h-5 w-5 text-amber-brand" />
              {editing ? "Editar recompensa" : "Nova recompensa"}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Nome, descrição, custo em pontos e uma imagem opcional (até 5MB).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3" data-testid="reward-form">
            <div>
              <Label className="text-xs font-mono-caps text-zinc-400">Nome</Label>
              <Input data-testid="reward-name-input" required maxLength={120} value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                className="mt-1 bg-zinc-900 border-zinc-800" placeholder="Troca de óleo cortesia" />
            </div>
            <div>
              <Label className="text-xs font-mono-caps text-zinc-400">Descrição</Label>
              <Textarea data-testid="reward-desc-input" rows={3} maxLength={500} value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                className="mt-1 bg-zinc-900 border-zinc-800" placeholder="Detalhes do que o cliente ganha" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono-caps text-zinc-400">Custo (pontos)</Label>
                <Input data-testid="reward-points-input" type="number" min="1" required value={form.points_cost}
                  onChange={(e) => setForm((p) => ({ ...p, points_cost: e.target.value }))}
                  className="mt-1 bg-zinc-900 border-zinc-800" placeholder="200" />
              </div>
              <div>
                <Label className="text-xs font-mono-caps text-zinc-400">Status</Label>
                <button
                  type="button"
                  data-testid="reward-active-toggle"
                  onClick={() => setForm((p) => ({ ...p, is_active: !p.is_active }))}
                  className={`mt-1 w-full h-10 rounded-md border text-sm font-bold uppercase tracking-wide ${form.is_active ? "border-green-brand text-green-300 bg-green-brand/10" : "border-zinc-800 text-zinc-400"}`}
                >
                  {form.is_active ? "Ativa" : "Inativa"}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono-caps text-zinc-400">Imagem (opcional)</Label>
              {editing?.has_image && !file && !removeImg && (
                <div className="mt-2 relative">
                  <RewardImage rewardId={editing.id} bust={bust} className="h-28 w-full rounded-lg" />
                  <button type="button" data-testid="reward-remove-image" onClick={() => setRemoveImg(true)}
                    className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/70 border border-zinc-700 text-zinc-200 flex items-center justify-center">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              <input ref={fileRef} data-testid="reward-image-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setRemoveImg(false); }} />
              <Button type="button" data-testid="reward-image-pick" onClick={() => fileRef.current?.click()}
                variant="ghost" className="mt-2 h-10 border border-zinc-800 text-zinc-200 hover:bg-zinc-900">
                <Upload className="h-4 w-4 mr-2" />
                {file ? file.name : (editing?.has_image && !removeImg ? "Trocar imagem" : "Selecionar imagem")}
              </Button>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" disabled={saving} onClick={() => setOpen(false)} className="text-zinc-300 hover:bg-zinc-900">Cancelar</Button>
              <Button data-testid="reward-submit-btn" type="submit" disabled={saving}
                className="bg-amber-brand hover:bg-amber-brandDark text-black font-bold uppercase">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Salvar" : "Criar recompensa")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
